/**
 * Score calculation module â€“ matches backend soft score (API "score").
 * See docs/reference/score-model.md for full spec; backend: gen/helpers/sa_helpers.py, sa_params.py, gen/engine/build.py.
 */

import * as st from './st';
import { normalizeDefenseShare } from './GenConfig';

// â€”â€” Constants (must match backend gen/helpers/sa_params.py) â€”â€”
const SCORE_BASE = 100.0;
const SCORE_DEN_MIN = 1.0;
const OVERSHOOT_MULTIPLIER = 0.25;
const BELOW_TARGET_POWER = 1.1;
const SCORING_POWER_MEAN_P = 0.5;
/** Spell-bucket only — must match backend P.scoring_spell_power_mean_p (0 = geometric). */
const SCORING_SPELL_POWER_MEAN_P = 0.0;
const DEFENSE_VITALITY_WEIGHT = 0.065;
const DEFENSE_VITALITY_NEGATIVE_WEIGHT = 5;
const DEFENSE_AVG_ELEMENTAL_RES_WEIGHT = 2.5;
const DEFENSE_MIN_ELEMENTAL_RES_WEIGHT = 5.5;
const DEFENSE_AVG_FIXED_ELEMENTAL_RES_WEIGHT = 0.25;
const DEFENSE_MIN_FIXED_ELEMENTAL_RES_WEIGHT = 0.5;
const DEFENSE_CRIT_RES_WEIGHT = 0.3;
const DEFENSE_PUSH_RES_WEIGHT = 0.15;
const DEFENSE_NEGATIVE_ELEMENTAL_RES_PENALTY = 10.0;
const DEFENSE_NEGATIVE_FIXED_ELEMENTAL_RES_PENALTY = 1.0;
const DEFENSE_NEGATIVE_FINAL_RES_PENALTY = 15.0;
const DEFENSE_NEGATIVE_CRIT_RES_PENALTY = 1.0;
const DEFENSE_NEGATIVE_PUSH_RES_PENALTY = 0.5;
const DEFENSE_POSITION_RES_ONE_SIDED_SHARE = 0.1;
const DEFENSE_POSITIONAL_TO_ELEMENTAL = 0.95;
export const SPELLS_WEIGHT = 12.0;
export const BASELINE_SPELL_FLOOR = 1.0;
export const SPELL_DPA_BOOST_WEIGHT = 0.4;
const OFFENSIVE_MULT_WEIGHT = 1.0;
// PR-2: variance penalty constants. Mirror of sa_scoring.py.
const VARIANCE_PENALTY_WEIGHT = 0;
const NATURAL_CRIT_CHANCE_PCT = 5.0;
const VARIANCE_CRIT_FLOOR_PCT = 10.0;
const VARIANCE_CRIT_CEILING_PCT = 90.0;
const NATURAL_CRIT_DAMAGE_GAP = 20.0;

/** Raw defense-bucket units (before the per-mode defense weight); must match core/item_balanced_bonuses.py */
const BALANCED_ITEM_DEFENSE_RAW = {
    737: 5.0, // Dofus Émeraude
    7112: 4.0, // Dofus Tacheté
    7115: 30.0, // Dofus Ivoire
    19398: 8.0, // Dofus Forgelave
    20286: 5.0, // Dofus Argenté Scintillant
    21451: 23.0, // Prytek Mate
    21452: 24.0, // Prytek Brillante
    21453: 25.0, // Prytek Iridescente
    22022: 15.0, // Aprybou
    23408: 25.0, // Dorigami
};

/**
 * Extra multiplier applied to the spell term of push-only criteria.
 * Must match backend gen/helpers/sa_params.py â†’ P.push_term_scale.
 */
export const PUSH_TERM_SCALE = 3.0;

export const DEFAULT_TARGET_WEIGHT = 1.0;

/**
 * The find-the-best-weapon term — mirrors `BestWeaponPool::term` in
 * `solver-core/src/weapon_pool.rs`.
 *
 * Two scales, deliberately. `value / reference` is on whichever metric the
 * criterion ranks on (damage per AP, or what a turn of hitting is worth), while
 * the efficiency factor always comes off the pool's damage per AP — so choosing
 * per-turn changes which weapon wins without also changing how much the
 * criterion counts against the spells beside it.
 *
 * @param {number} value the weapon's worth on the metric, for this build
 * @param {number} reference the pool's best naked value on that same metric
 * @param {number} referenceDpa the pool's best naked damage per AP
 */
export function computeBestWeaponTermValue(value, reference, referenceDpa) {
    const v = Number(value) || 0;
    const ref = Math.max(Number(reference) || 0, BASELINE_SPELL_FLOOR);
    const refDpa = Math.max(Number(referenceDpa) || 0, 0);
    const naturalEfficiencyBoost = 1 + SPELL_DPA_BOOST_WEIGHT * Math.sqrt(refDpa);
    return SPELLS_WEIGHT * (v / ref) * naturalEfficiencyBoost;
}

export function computeSpellTermValue(primary, baseline, apCost = 1, pushScale = 1.0) {
    const p = Number(primary) || 0;
    const b = Number(baseline) || 0;
    const ap = Math.max(Number(apCost) || 1, 1);
    const scale = Number(pushScale) || 1.0;
    const denom = Math.max(b, BASELINE_SPELL_FLOOR);
    const ratioComponent = p / denom;
    const baselineDpa = Math.max(b, 0) / ap;
    const naturalEfficiencyBoost = 1 + SPELL_DPA_BOOST_WEIGHT * Math.sqrt(baselineDpa);
    return SPELLS_WEIGHT * scale * ratioComponent * naturalEfficiencyBoost;
}

// Soft caps for scoring (gen/engine/build.py â†’ Build.SOFT_CAPS). Charac ID â†’ cap.
export const SOFT_CAPS = {
    [st.AP_ID]: 12,
    [st.MP_ID]: 6,
    [st.RANGE_ID]: 6,
    [st.SP_ID]: 6,
    37: 50, // % Neutral Resistance
    33: 50, // % Earth
    34: 50, // % Fire
    35: 50, // % Water
    36: 50, // % Air
};

// Effective stat value: elemental stats and damage (docs Â§4.3)
const ELEMENTAL_STAT_IDS = [st.STRENGTH_ID, st.INTELLIGENCE_ID, st.CHANCE_ID, st.AGILITY_ID];
const ELEMENTAL_DAMAGE_IDS = [92, 88, 89, 90, 91]; // neutral, earth, fire, water, air

const ELEMENTAL_STAT_SET = new Set(ELEMENTAL_STAT_IDS);
const ELEMENTAL_DAMAGE_SET = new Set(ELEMENTAL_DAMAGE_IDS);
const FINAL_DAMAGES_FAMILY = new Set([st.FINAL_DAMAGE_ID, st.SPELL_PER_DMG_ID, 122, 120, 125]);

// ─── Offensive layer (PR-1: partition + damage% multiplier) ─────────────────
// Mirror of OFFENSIVE_CHARAC_IDS in backend/gen/helpers/sa_scoring.py.
// Edit in lockstep with the backend.
export const OFFENSIVE_CHARAC_IDS = new Set([
    ...ELEMENTAL_STAT_IDS,
    st.POWER_ID,
    st.DAMAGE_ID,
    st.CRIT_DAMAGE_ID,
    ...ELEMENTAL_DAMAGE_IDS,
    ...FINAL_DAMAGES_FAMILY,
    st.CRIT_ID,
]);

// Damage% multiplier stat ids — mirror of OFFENSIVE_MULT_PCT_IDS in backend.
// Flat "Dommages" (st.DAMAGE_ID) is intentionally excluded: it is flat-additive,
// not a percent, and is already folded into requested elemental-damage rows.
// Only three of the five reach the multiplier — see computeOffensiveMultFactor.
// % Weapon (122) is deliberately absent: it multiplies weapon hits and nothing
// else, and a stats-only request scores none. spellUtils' weapon path picks % armes
// OR % sorts, never both; with no criterion the same else-branch applies.
const OFFENSIVE_MULT_UNCONDITIONAL_IDS = [st.FINAL_DAMAGE_ID, st.SPELL_PER_DMG_ID];
const OFFENSIVE_MULT_POSITION_IDS = [st.MELEE_PER_DMG_ID, st.RANGED_PER_DMG_ID];

function isOffensiveRequest(targets, spellTerms) {
    if (Array.isArray(spellTerms) && spellTerms.length > 0) return true;
    if (!targets) return false;
    for (const k of Object.keys(targets)) {
        if (OFFENSIVE_CHARAC_IDS.has(Number(k))) return true;
    }
    return false;
}

/** Game crit % implied by build crit stat (5% natural floor). Mirror of spell EV base + stat. */
export function gameCritPctFromStat(critStat) {
    return Math.min(100, Math.max(NATURAL_CRIT_CHANCE_PCT, NATURAL_CRIT_CHANCE_PCT + (Number(critStat) || 0)));
}

/**
 * Variance penalty on the offensive partition (mirror of _compute_variance_factor).
 * @returns {{ factor: number, penaltyPct: number, gameCritPct: number, applies: boolean }}
 */
export function computeVarianceMeta(cappedStats, applies = true) {
    const critStat = Number(cappedStats?.[st.CRIT_ID]) || 0;
    const gameCritPct = gameCritPctFromStat(critStat);
    const noop = { factor: 1, penaltyPct: 0, gameCritPct, applies: false };
    if (!applies) return noop;

    if (gameCritPct <= VARIANCE_CRIT_FLOOR_PCT || gameCritPct >= VARIANCE_CRIT_CEILING_PCT) {
        return { factor: 1, penaltyPct: 0, gameCritPct, applies: true };
    }
    const span = VARIANCE_CRIT_CEILING_PCT - VARIANCE_CRIT_FLOOR_PCT;
    const q = (gameCritPct - VARIANCE_CRIT_FLOOR_PCT) / span;
    const sigmaF = Math.sqrt(q * (1 - q));
    if (sigmaF < 1e-9) return { factor: 1, penaltyPct: 0, gameCritPct, applies: true };

    const cdStat = Math.max(0, Number(cappedStats?.[st.CRIT_DAMAGE_ID]) || 0);
    const cdEffective = cdStat + NATURAL_CRIT_DAMAGE_GAP;
    const factor = Math.max(0, 1 - VARIANCE_PENALTY_WEIGHT * sigmaF * cdEffective / 100);
    const penaltyPct = (1 - factor) * 100;
    return { factor, penaltyPct, gameCritPct, applies: true };
}

export function computeVarianceFactor(cappedStats) {
    return computeVarianceMeta(cappedStats, true).factor;
}

export function computeOffensiveMultFactor(cappedStats, baselineStats, statsCriteriaKeys, position = st.POSITION_NONE) {
    // Clamp(1 + (Σ damage% delta / 100) × OFFENSIVE_MULT_WEIGHT, 0, ∞).
    //
    // % Final/Spell add. % Melee or % Ranged adds only on the side the
    // None/Mêlée/Distance toggle declares (`position`), and neither does when
    // it declares none. Same rule positionDamageMultiplier in spellUtils.js
    // uses for spells.
    //
    // User-listed stats drop out to avoid double-counting with the partition;
    // the positional pair drops out as a unit, since a user who names % Melee
    // has declared a side and % Ranged is worth nothing to them.
    const deltaOf = (cid) => {
        const val = Number(cappedStats?.[cid]) || 0;
        const base = baselineStats ? (Number(baselineStats[cid]) || 0) : 0;
        return val - base;
    };
    let delta = 0;
    for (const cid of OFFENSIVE_MULT_UNCONDITIONAL_IDS) {
        if (statsCriteriaKeys.has(Number(cid))) continue;
        delta += deltaOf(cid);
    }
    if (!OFFENSIVE_MULT_POSITION_IDS.some((cid) => statsCriteriaKeys.has(Number(cid)))) {
        const id = st.POSITION_DAMAGE_ID[position];
        if (id != null) delta += deltaOf(id);
    }
    if (Math.abs(delta) < 1e-9) return 1;
    const factor = 1 + (delta / 100) * OFFENSIVE_MULT_WEIGHT;
    return Math.max(0, factor);
}
const FINAL_RESISTANCES_FAMILY = new Set([124, 121, 141, 142]);
const ELEM_RES_PERCENT_IDS = [37, 33, 34, 35, 36];
const PRIMARY_ELEM_RES_PERCENT_IDS = [33, 34, 35, 36];
const ELEM_RES_FIXED_IDS = [58, 54, 55, 56, 57];
const PRIMARY_ELEM_RES_FIXED_IDS = [54, 55, 56, 57];
const FINAL_RES_IDS = [124, 121, 141, 142];

function balancedItemDefenseRawTotal(equippedItemIds) {
    if (!Array.isArray(equippedItemIds) || equippedItemIds.length === 0) return 0;
    let total = 0;
    for (const iid of equippedItemIds) {
        if (!iid) continue;
        const n = Number(iid);
        total += BALANCED_ITEM_DEFENSE_RAW[n] ?? 0;
    }
    return total;
}

/**
 * Build final stats map (charId â†’ value) from build's getStats().
 * Excludes level; uses .current for each stat.
 * @param {import('./Build').default} build
 * @returns {Record<number, number>}
 */
export function getFinalStatsFromBuild(build, { useBoosts = false } = {}) {
    if (!build) return {};
    // The sheet the solver searches from (Build.getStats solverView): with
    // boosts as natural stats when the generator setting says so.
    const stats = build.getStats({ includePassiveEffects: true, solverView: true, useBoosts });
    const out = {};
    for (const [idStr, obj] of Object.entries(stats)) {
        const id = Number(idStr);
        if (id === st.LVL_ID) continue;
        const v = obj && typeof obj.current === 'number' ? obj.current : 0;
        out[id] = v;
    }
    return out;
}

/**
 * Build naked/baseline stats (natural + scrolls + the exos generations place),
 * with same caps/derived lines as runtime stats. The exos match the +1 the
 * kernel request counts (kernelRequest.placedExoBonus), so the score here is
 * the solver's.
 * @param {import('./Build').default} build
 * @returns {Record<number, number>}
 */
export function getBaselineStatsFromBuild(build) {
    if (!build) return {};
    const lvl = typeof build.getLvl === 'function' ? Number(build.getLvl()) : Number(build.lvl);
    if (!Number.isFinite(lvl)) return {};
    const natural = st.getNaturalStats(lvl) || {};
    const scrolls = build.scrolls || {};
    const exos = Object.fromEntries((build.fmExos || []).map((id) => [Number(id), 1]));
    const out = {};
    st.STATS.forEach((stat) => {
        const id = Number(stat.id);
        if (id === st.LVL_ID) return;
        out[id] = Number(natural[id] || 0) + Number(scrolls[id] || 0) + Number(exos[id] || 0);
    });

    // Mirror build caps before derived bonuses.
    if ((out[st.AP_ID] ?? 0) > 12) out[st.AP_ID] = 12;
    if ((out[st.MP_ID] ?? 0) > 6) out[st.MP_ID] = 6;
    if ((out[st.RANGE_ID] ?? 0) > 6) out[st.RANGE_ID] = 6;
    if ((out[st.SP_ID] ?? 0) > 6) out[st.SP_ID] = 6;
    ELEM_RES_PERCENT_IDS.forEach((id) => {
        if ((out[id] ?? 0) > 50) out[id] = 50;
    });

    const str = Number(out[st.STRENGTH_ID] || 0);
    const int = Number(out[st.INTELLIGENCE_ID] || 0);
    const cha = Number(out[st.CHANCE_ID] || 0);
    const agi = Number(out[st.AGILITY_ID] || 0);
    const wis = Number(out[st.WISDOM_ID] || 0);
    const iniFromStats = str + int + cha + agi;
    out[st.INITIATIVE_ID] = Number(out[st.INITIATIVE_ID] || 0) + iniFromStats;
    const wisdomBonus = Math.floor(wis / 10);
    out[st.AP_REDUCTION_ID] = Number(out[st.AP_REDUCTION_ID] || 0) + wisdomBonus;
    out[st.MP_REDUCTION_ID] = Number(out[st.MP_REDUCTION_ID] || 0) + wisdomBonus;
    out[st.AP_PARRY_ID] = Number(out[st.AP_PARRY_ID] || 0) + wisdomBonus;
    out[st.MP_PARRY_ID] = Number(out[st.MP_PARRY_ID] || 0) + wisdomBonus;
    const agilityBonus = Math.floor(agi / 10);
    out[st.LOCK_ID] = Number(out[st.LOCK_ID] || 0) + agilityBonus;
    out[st.DODGE_ID] = Number(out[st.DODGE_ID] || 0) + agilityBonus;
    out[st.PROSPECTING_ID] = Number(out[st.PROSPECTING_ID] || 0) + Math.floor(cha / 10);
    out[st.PODS_ID] = Number(out[st.PODS_ID] || 0) + (str * 5);
    return out;
}

/**
 * Apply soft caps in-place (mutates). Use a copy if you need to keep original.
 * @param {Record<number, number>} statsByCharId
 * @returns {Record<number, number>} same object after clamping
 */
export function applySoftCaps(statsByCharId) {
    for (const [charIdStr, cap] of Object.entries(SOFT_CAPS)) {
        const charId = Number(charIdStr);
        if (charId in statsByCharId && statsByCharId[charId] > cap) {
            statsByCharId[charId] = cap;
        }
    }
    return statsByCharId;
}

/**
 * Effective value for scoring: add Power to elemental stats, Damage to elemental damage, Crit Damage when allowed (docs Â§4.3).
 * @param {Record<number, number>} cappedStats â€“ stats after soft caps (charId â†’ value)
 * @param {number} charId
 * @param {boolean} allowPower
 * @param {boolean} critDamCountsAsDamage
 * @returns {number}
 */
export function getEffectiveStatValue(cappedStats, charId, allowPower, critDamCountsAsDamage) {
    const raw = cappedStats[charId] ?? 0;
    let v = raw;

    if (allowPower && ELEMENTAL_STAT_SET.has(charId)) {
        v += cappedStats[st.POWER_ID] ?? 0;
    }
    if (ELEMENTAL_DAMAGE_SET.has(charId)) {
        v += cappedStats[st.DAMAGE_ID] ?? 0;
        if (critDamCountsAsDamage) v += cappedStats[st.CRIT_DAMAGE_ID] ?? 0;
        return v;
    }
    if (charId === st.DAMAGE_ID) {
        if (critDamCountsAsDamage) v += cappedStats[st.CRIT_DAMAGE_ID] ?? 0;
        return v;
    }
    return v;
}

/**
 * Max soft score = sum over soft targets only of weight * goal (docs Â§5.3).
 * @param {Record<number, { min?: number, weight?: number, hard?: boolean }>} targets â€“ charId â†’ { min, weight?, hard? }
 * @returns {number}
 */
export function computeMaxSoftScore(targets) {
    let maxSoft = 0;
    if (!targets || typeof targets !== 'object') return maxSoft;
    for (const [charIdStr, rule] of Object.entries(targets)) {
        if (rule && rule.maximize) continue;
        if (rule && rule.hard) continue;
        const charId = Number(charIdStr);
        const goal = typeof rule.min === 'number' ? rule.min : 0;
        const w = typeof rule.weight === 'number' ? rule.weight : (st.STAT_WEIGHTS_MAP[charId] ?? DEFAULT_TARGET_WEIGHT);
        maxSoft += w * goal;
    }
    return maxSoft;
}

/**
 * Raw soft score and hard count (docs Â§5.4).
 * Below target: contribution uses signed power so negative values can penalize score.
 * At/above goal: contribution = weight * goal; overshoot: += weight * OVERSHOOT_MULTIPLIER * goal * (over / (over + goal)) (saturating, goal-relative).
 * @param {Record<number, number>} cappedStats
 * @param {Record<number, { min?: number, weight?: number, hard?: boolean }>} targets
 * @param {boolean} allowPower
 * @param {boolean} critDamCountsAsDamage
 * @returns {{ rawSoft: number, hardCount: number }}
 */
export function computeRawSoftAndHard(cappedStats, targets, allowPower, critDamCountsAsDamage, baselineStats = null) {
    let rawSoft = 0;
    let hardCount = 0;
    const statTerms = [];
    const statTermDetails = [];
    if (!targets || typeof targets !== 'object') return { rawSoft, hardCount };

    for (const [charIdStr, rule] of Object.entries(targets)) {
        const charId = Number(charIdStr);
        const goal = typeof rule.min === 'number' ? rule.min : 0;
        const w = typeof rule.weight === 'number' ? rule.weight : (st.STAT_WEIGHTS_MAP[charId] ?? DEFAULT_TARGET_WEIGHT);
        // What one point is worth (`w`) and how much the user cares
        // (`importance`) are different things and must not be multiplied
        // together: `w` scales the term, importance weights the mean.
        // `computeMaxSoftScore` deliberately ignores importance — it normalises
        // `normalizedStatsScore`, which is reported but never scored.
        const importance = Number.isFinite(Number(rule.importance)) ? Number(rule.importance) : 1;
        const valRaw = getEffectiveStatValue(cappedStats, charId, allowPower, critDamCountsAsDamage);
        const baseVal = baselineStats
            ? getEffectiveStatValue(baselineStats, charId, allowPower, critDamCountsAsDamage)
            : 0;
        const val = valRaw - baseVal;
        const goalAdjusted = Math.max(0, goal - baseVal);

        if (rule?.maximize) {
            const term = w * val;
            statTerms.push(term);
            statTermDetails.push({
                charId,
                value: val,
                goal: null,
                weight: w,
                importance,
                term,
                maximize: true,
                hard: false,
            });
            rawSoft += term;
            continue;
        }

        const safeAdjustedGoal = Math.max(goalAdjusted, 1);
        let term = 0;
        if (val >= goalAdjusted) {
            term += w * goalAdjusted;
        } else if (goalAdjusted > 0) {
            term += w * goalAdjusted * signedPow(val / safeAdjustedGoal, BELOW_TARGET_POWER);
        } else {
            term += w * signedPow(val, BELOW_TARGET_POWER);
        }
        if (val > goalAdjusted && goalAdjusted > 0) {
            // Saturating, goal-relative overshoot bonus (mirrors backend
            // sa_scoring.score_targets). Bounded by w*goal*OVERSHOOT_MULTIPLIER.
            const overshoot = val - goalAdjusted;
            term += w * OVERSHOOT_MULTIPLIER * goalAdjusted * (overshoot / (overshoot + goalAdjusted));
        }
        statTerms.push(term);
        statTermDetails.push({
            charId,
            value: val,
            goal: goalAdjusted,
            weight: w,
            importance,
            term,
            maximize: false,
            hard: !!rule?.hard,
        });
        rawSoft += term;

        if (rule?.hard) {
            if (valRaw >= goal) hardCount += 1;
        }
    }
    return { rawSoft, hardCount, statTerms, statTermDetails };
}

/**
 * Normalize to score in [0, 100] scale (docs Â§5.5).
 * score = (raw_soft / max(max_soft_score, SCORE_DEN_MIN)) * SCORE_BASE
 * @param {number} rawSoft
 * @param {number} maxSoftScore
 * @returns {number}
 */
export function normalizeScore(rawSoft, maxSoftScore) {
    const denom = Math.max(maxSoftScore, SCORE_DEN_MIN);
    return (rawSoft / denom) * SCORE_BASE;
}

function signedPow(value, p) {
    const n = Number(value) || 0;
    if (n === 0) return 0;
    return Math.sign(n) * (Math.abs(n) ** p);
}

/**
 * A bucket's own importance: the MEAN of its members', not the sum. The mean is
 * what keeps an unweighted request identical — every bucket comes out at 1.0
 * however many criteria it holds, which is the equal-bucket treatment this
 * always had. A sum would weight buckets by how many criteria they contain.
 */
export function meanImportance(weights) {
    if (!Array.isArray(weights) || weights.length === 0) return 1;
    return weights.reduce((a, b) => a + (Number(b) || 0), 0) / weights.length;
}

/**
 * `powerMean` with a per-term importance (the stats/spells weights column).
 * Mirrors solver-core `score::weighted_power_mean` — read the comment there for
 * why importance weights the mean instead of scaling the term.
 *
 * Equal weights reproduce `powerMean` exactly, so an unweighted request scores
 * bit-identically to before this existed.
 */
export function weightedPowerMean(terms, weights, p = SCORING_POWER_MEAN_P) {
    if (!Array.isArray(terms) || terms.length === 0) return 0;
    if (terms.length === 1) return Number(terms[0]) || 0;
    const w = Array.isArray(weights) ? weights : [];
    const ws = terms.map((_, i) => {
        const v = Number(w[i]);
        return Number.isFinite(v) ? v : 1;
    });
    if (ws.every((v) => v === 1)) return powerMean(terms, p);
    const total = ws.reduce((a, b) => a + b, 0);
    if (!(total > 0)) return powerMean(terms, p);
    const values = terms.map((t) => Number(t) || 0);
    if (Math.abs(p) < 1e-9) {
        let sum = 0;
        values.forEach((t, i) => {
            if (t === 0) return;
            sum += ws[i] * Math.sign(t) * Math.log(Math.max(Math.abs(t), 1e-9));
        });
        const logAvg = sum / total;
        return Math.sign(logAvg) * Math.exp(Math.abs(logAvg));
    }
    const m = values.reduce((acc, t, i) => acc + ws[i] * signedPow(t, p), 0) / total;
    return signedPow(m, 1 / p);
}

export function powerMean(terms, p = SCORING_POWER_MEAN_P) {
    if (!Array.isArray(terms) || terms.length === 0) return 0;
    if (terms.length === 1) return Number(terms[0]) || 0;
    const values = terms.map((t) => Number(t) || 0);
    if (Math.abs(p) < 1e-9) {
        const signedLogs = values.map((t) => {
            if (t === 0) return 0;
            return Math.sign(t) * Math.log(Math.max(Math.abs(t), 1e-9));
        });
        const logAvg = signedLogs.reduce((s, t) => s + t, 0) / signedLogs.length;
        return Math.sign(logAvg) * Math.exp(Math.abs(logAvg));
    }
    const m = values.reduce((s, t) => s + signedPow(t, p), 0) / values.length;
    return signedPow(m, 1 / p);
}

export function calculateDefenseScore(statsByCharId, baselineStatsByCharId = null) {
    const val = (id) => Number(statsByCharId?.[id]) || 0;
    const baselineVit = Math.max(0, Number(baselineStatsByCharId?.[st.VITALITY_ID]) || 0);
    const vitalityDelta = val(st.VITALITY_ID) - baselineVit;
    const elemRes = ELEM_RES_PERCENT_IDS.map(val);
    const primaryElemRes = PRIMARY_ELEM_RES_PERCENT_IDS.map(val);
    const avgElem = elemRes.reduce((s, v) => s + v, 0) / elemRes.length;
    const minElem = Math.min(...primaryElemRes);
    const fixedRes = ELEM_RES_FIXED_IDS.map(val);
    const primaryFixedRes = PRIMARY_ELEM_RES_FIXED_IDS.map(val);
    const avgFixed = fixedRes.reduce((s, v) => s + v, 0) / fixedRes.length;
    const minFixed = Math.min(...primaryFixedRes);
    const rawCritRes = val(87);
    const rawPushRes = val(85);
    const rawMeleeRes = val(124);
    const rawRangedRes = val(121);
    const critRes = Math.max(0, rawCritRes);
    const pushRes = Math.max(0, rawPushRes);
    const meleeRes = Math.max(0, rawMeleeRes);
    const rangedRes = Math.max(0, rawRangedRes);
    const coveredPosRes = Math.min(meleeRes, rangedRes);
    const oneSidedPosRes = Math.max(meleeRes, rangedRes) - coveredPosRes;
    const effectivePosRes = coveredPosRes + DEFENSE_POSITION_RES_ONE_SIDED_SHARE * oneSidedPosRes;
    const negativeElem = elemRes.reduce((s, v) => s + Math.max(0, -v), 0);
    const negativeFixed = fixedRes.reduce((s, v) => s + Math.max(0, -v), 0);
    const negativeFinal = FINAL_RES_IDS.reduce((s, id) => s + Math.max(0, -val(id)), 0);
    const negativeCrit = Math.max(0, -rawCritRes);
    const negativePush = Math.max(0, -rawPushRes);

    let hpTerm = 0;
    if (vitalityDelta > 0) {
        hpTerm = DEFENSE_VITALITY_WEIGHT * vitalityDelta;
    } else if (vitalityDelta < 0) {
        hpTerm = -DEFENSE_VITALITY_NEGATIVE_WEIGHT * (-vitalityDelta);
    }
    const avgResTerm = DEFENSE_AVG_ELEMENTAL_RES_WEIGHT * avgElem;
    const minResTerm = DEFENSE_MIN_ELEMENTAL_RES_WEIGHT * minElem;
    const avgFixedTerm = DEFENSE_AVG_FIXED_ELEMENTAL_RES_WEIGHT * avgFixed;
    const minFixedTerm = DEFENSE_MIN_FIXED_ELEMENTAL_RES_WEIGHT * minFixed;
    const positionalResWeight = DEFENSE_POSITIONAL_TO_ELEMENTAL * (DEFENSE_AVG_ELEMENTAL_RES_WEIGHT + DEFENSE_MIN_ELEMENTAL_RES_WEIGHT);
    const positionalResTerm = positionalResWeight * effectivePosRes;
    const critTerm = (
        DEFENSE_CRIT_RES_WEIGHT * critRes
        - DEFENSE_NEGATIVE_CRIT_RES_PENALTY * negativeCrit
    );
    const pushTerm = (
        DEFENSE_PUSH_RES_WEIGHT * pushRes
        - DEFENSE_NEGATIVE_PUSH_RES_PENALTY * negativePush
    );
    const penalty = (
        DEFENSE_NEGATIVE_ELEMENTAL_RES_PENALTY * negativeElem
        + DEFENSE_NEGATIVE_FIXED_ELEMENTAL_RES_PENALTY * negativeFixed
        + DEFENSE_NEGATIVE_FINAL_RES_PENALTY * negativeFinal
    );
    const score = hpTerm + avgResTerm + minResTerm + avgFixedTerm + minFixedTerm + positionalResTerm + critTerm + pushTerm - penalty;
    const details = [
        { key: 'vitality', label: { fr: 'Vitalité', en: 'Vitality', de: 'Vitalität', es: 'Vitalidad', pt: 'Vitalidade' }, value: vitalityDelta, term: hpTerm },
        { key: 'avg_elemental_res', label: { fr: 'Rés. élémentaires moy.', en: 'Avg elemental res.', de: 'Ø Elementar-Res.', es: 'Res. elementales med.', pt: 'Res. elementais méd.' }, value: avgElem, term: avgResTerm },
        { key: 'min_elemental_res', label: { fr: 'Rés. élémentaire min.', en: 'Min elemental res.', de: 'Min. Elementar-Res.', es: 'Res. elemental mín.', pt: 'Res. elemental mín.' }, value: minElem, term: minResTerm },
        { key: 'avg_fixed_elemental_res', label: { fr: 'Rés. fixes moy.', en: 'Avg fixed res.', de: 'Ø Feste Res.', es: 'Res. fijas med.', pt: 'Res. fixas méd.' }, value: avgFixed, term: avgFixedTerm },
        { key: 'min_fixed_elemental_res', label: { fr: 'Rés. fixe min.', en: 'Min fixed res.', de: 'Min. feste Res.', es: 'Res. fija mín.', pt: 'Res. fixa mín.' }, value: minFixed, term: minFixedTerm },
        { key: 'positional_res', label: { fr: 'Rés. mêlée/dist.', en: 'Melee/ranged res.', de: 'Nah-/Fernkampf-Res.', es: 'Res. c. a c./distancia', pt: 'Res. corpo a corpo/distância' }, value: effectivePosRes, term: positionalResTerm },
        { key: 'crit_res', label: { fr: 'Rés. critique', en: 'Critical res.', de: 'Kritisch-Res.', es: 'Res. crítica', pt: 'Res. crítica' }, value: rawCritRes, term: critTerm },
        { key: 'push_res', label: { fr: 'Rés. poussée', en: 'Pushback res.', de: 'Schubs-Res.', es: 'Res. a empuje', pt: 'Res. a empurrão' }, value: rawPushRes, term: pushTerm },
    ];
    if (penalty) {
        details.push({ key: 'negative_res_penalty', label: { fr: 'Malus résistances', en: 'Resistance penalty', de: 'Resistenz-Malus', es: 'Penalización de resistencias', pt: 'Penalidade de resistências' }, value: penalty, term: -penalty });
    }
    return { score, details };
}

function normalizeTargetWeightsByFamily(targets) {
    if (!targets || typeof targets !== 'object') return targets;
    const normalized = {};
    Object.entries(targets).forEach(([k, rule]) => {
        normalized[Number(k)] = { ...(rule || {}) };
    });
    const families = [
        new Set(ELEMENTAL_STAT_IDS),
        new Set(ELEMENTAL_DAMAGE_IDS),
        FINAL_DAMAGES_FAMILY,
        FINAL_RESISTANCES_FAMILY,
    ];
    families.forEach((family) => {
        const members = Object.keys(normalized)
            .map(Number)
            .filter((id) => family.has(id));
        if (members.length <= 1) return;
        const weights = members.map((id) => {
            const w = Number(normalized[id]?.weight);
            return Number.isFinite(w) ? w : DEFAULT_TARGET_WEIGHT;
        });
        const total = weights.reduce((a, b) => a + Math.max(0, b), 0);
        if (!(total > 0)) return;
        const budget = Math.max(...weights);
        const scale = budget / total;
        members.forEach((id) => {
            const w = Number(normalized[id]?.weight);
            const ww = Number.isFinite(w) ? w : DEFAULT_TARGET_WEIGHT;
            normalized[id].weight = ww * scale;
        });
    });
    return normalized;
}

function splitFiniteAndMaximizeTargets(targets) {
    const finite = {};
    const maximize = {};
    const floors = {};
    if (!targets || typeof targets !== 'object') return { finite, maximize, floors };
    Object.entries(targets).forEach(([k, rule]) => {
        const id = Number(k);
        if (!Number.isFinite(id)) return;
        const row = { ...(rule || {}) };
        if (row.floor) floors[id] = row;
        else if (row.maximize) maximize[id] = row;
        else finite[id] = row;
    });
    return { finite, maximize, floors };
}

/**
 * What the floors a build misses cost: importance · weight · deficit^α each,
 * nothing for a floor it meets. Kept out of the criteria means — as a term a
 * met floor would score 0 and still drag its bucket down. Read on the absolute
 * sheet value. Mirrors solver-core `score::floor_penalty`.
 * @returns {{ penalty: number, details: Array<object> }}
 */
export function computeFloorPenalty(cappedStats, floors, allowPower, critDamCountsAsDamage) {
    let penalty = 0;
    const details = [];
    Object.entries(floors || {}).forEach(([charIdStr, rule]) => {
        const charId = Number(charIdStr);
        const goal = Number(rule?.min) || 0;
        const w = typeof rule?.weight === 'number' ? rule.weight : (st.STAT_WEIGHTS_MAP[charId] ?? DEFAULT_TARGET_WEIGHT);
        const importance = Number.isFinite(Number(rule?.importance)) ? Number(rule.importance) : 1;
        const value = getEffectiveStatValue(cappedStats, charId, allowPower, critDamCountsAsDamage);
        const deficit = goal - value;
        const cost = deficit > 0 ? importance * w * (deficit ** BELOW_TARGET_POWER) : 0;
        penalty += cost;
        details.push({ charId, value, goal, weight: w, importance, term: -cost, floor: true });
    });
    return { penalty, details };
}

/**
 * Compute build soft score (same value as API "score").
 * Steps: final stats from build â†’ apply soft caps â†’ max_soft_score from targets â†’ raw_soft from effective values â†’ normalize.
 * @param {import('./Build').default} build
 * @param {Record<number, { min?: number, weight?: number, hard?: boolean }>} targets â€“ e.g. from build.getActiveStats() converted to target rules
 * @param {{ allowPower?: boolean, critDamCountsAsDamage?: boolean, position?: string }} options â€“ match gen request settings
 * @returns {{ score: number, baseScore: number, criteriaScore: number, defenseRawScore: number|null, defenseShare: number|null, rawSoft: number, maxSoftScore: number, hardCount: number }}
 */
export function computeScore(build, targets, options = {}) {
    const allowPower = options.allowPower !== false;
    const critDamCountsAsDamage = !!options.critDamCountsAsDamage;
    const spellTerms = Array.isArray(options.spellTerms)
        ? options.spellTerms.filter((v) => Number.isFinite(v))
        : [];
    // Per-spell importance, positionally matched to spellTerms. Missing or
    // short means "all 1", which is the pre-weights behaviour.
    const spellImportance = spellTerms.map((_, i) => {
        const v = Number(Array.isArray(options.spellImportance) ? options.spellImportance[i] : NaN);
        return Number.isFinite(v) ? v : 1;
    });
    const spellTermDetails = Array.isArray(options.spellTermDetails)
        ? options.spellTermDetails
        : [];

    const finalStats = getFinalStatsFromBuild(build, { useBoosts: options.useBoosts === true });
    const cappedStats = applySoftCaps({ ...finalStats });
    const baselineStats = applySoftCaps({ ...getBaselineStatsFromBuild(build) });

    const { finite, maximize, floors } = splitFiniteAndMaximizeTargets(targets);
    const normalizedFiniteTargets = normalizeTargetWeightsByFamily(finite);
    const normalizedMaximizeTargets = normalizeTargetWeightsByFamily(maximize);
    const normalizedTargets = { ...normalizedFiniteTargets, ...normalizedMaximizeTargets };

    const maxSoftScore = computeMaxSoftScore(normalizedFiniteTargets);
    const { rawSoft, hardCount, statTerms = [], statTermDetails = [] } = computeRawSoftAndHard(
        cappedStats,
        normalizedTargets,
        allowPower,
        critDamCountsAsDamage,
        baselineStats
    );
    const normalizedStatsScore = normalizeScore(rawSoft, maxSoftScore);
    const defenseShare = normalizeDefenseShare(options.defenseShare);

    // ─── Partition stat terms into offensive vs non-offensive ───
    const offensiveTerms = [];
    const nonOffensiveTerms = [];
    const offensiveWeights = [];
    const nonOffensiveWeights = [];
    const offensiveTermDetails = [];
    const nonOffensiveTermDetails = [];
    const importanceOf = (d) => (Number.isFinite(Number(d?.importance)) ? Number(d.importance) : 1);
    statTermDetails.forEach((d, i) => {
        const term = Number.isFinite(d?.term) ? d.term : Number(statTerms[i]) || 0;
        if (OFFENSIVE_CHARAC_IDS.has(Number(d?.charId))) {
            offensiveTerms.push(term);
            offensiveWeights.push(importanceOf(d));
            offensiveTermDetails.push(d);
        } else {
            nonOffensiveTerms.push(term);
            nonOffensiveWeights.push(importanceOf(d));
            nonOffensiveTermDetails.push(d);
        }
    });
    const statWeights = statTermDetails.map(importanceOf);

    let offensiveMean = offensiveTerms.length ? weightedPowerMean(offensiveTerms, offensiveWeights) : 0;
    const nonOffensiveMean = nonOffensiveTerms.length ? weightedPowerMean(nonOffensiveTerms, nonOffensiveWeights) : 0;
    let spellMean = spellTerms.length
        ? weightedPowerMean(spellTerms, spellImportance, SCORING_SPELL_POWER_MEAN_P)
        : 0;
    const statMean = statTerms.length ? weightedPowerMean(statTerms, statWeights) : 0;  // back-compat: full stat power_mean

    // ─── Build-level offensive adjustments ───
    // 1. Damage% multiplier (stats-criteria path only).
    // 2. Variance penalty (both paths, after the multiplier).
    const criteriaShare = 1 - defenseShare;
    let offensiveMultFactor = 1;
    if (criteriaShare > 0 && spellTerms.length === 0 && offensiveTerms.length > 0) {
        const statsCriteriaKeys = new Set([
            ...Object.keys(normalizedFiniteTargets || {}).map(Number),
            ...Object.keys(normalizedMaximizeTargets || {}).map(Number),
        ]);
        offensiveMultFactor = computeOffensiveMultFactor(cappedStats, baselineStats, statsCriteriaKeys, options.position);
        offensiveMean *= offensiveMultFactor;
    }

    const varianceApplies = criteriaShare > 0 && isOffensiveRequest(normalizedTargets, spellTerms);
    const varianceMeta = computeVarianceMeta(cappedStats, varianceApplies);
    if (varianceApplies && varianceMeta.factor !== 1) {
        offensiveMean *= varianceMeta.factor;
        spellMean *= varianceMeta.factor;
    }

    // ─── Combine partitions ───
    const parts = [];
    const partWeights = [];
    if (offensiveTerms.length) { parts.push(offensiveMean); partWeights.push(meanImportance(offensiveWeights)); }
    if (nonOffensiveTerms.length) { parts.push(nonOffensiveMean); partWeights.push(meanImportance(nonOffensiveWeights)); }
    if (spellTerms.length) { parts.push(spellMean); partWeights.push(meanImportance(spellImportance)); }
    // Weighted: a bucket holding one criterion returns that term untouched, so
    // without cross-bucket weighting "×10 on Agility, ×0.1 on one spell" would
    // combine two unweighted means equally and the request would mean nothing.
    let score = parts.length === 0 ? 0 : (parts.length === 1 ? parts[0] : weightedPowerMean(parts, partWeights));
    const { penalty: floorPenalty, details: floorTermDetails } = computeFloorPenalty(
        cappedStats, floors, allowPower, critDamCountsAsDamage,
    );
    score -= floorPenalty;

    const criteriaScore = score;
    let criteriaContribution = criteriaScore;

    let defenseScore = null;
    let defenseTermDetails = [];
    let defenseRawScore = null;
    if (defenseShare > 0 && (statTerms.length || spellTerms.length || floorTermDetails.length)) {
        const defense = calculateDefenseScore(cappedStats, baselineStats);
        // `itemIds` when the Build carries it: a build resolves its equipment
        // from the local catalog, and if that has not loaded the resolved
        // `items` are all null while the ids survive. Reading the ids keeps the
        // defense term correct in that window instead of scoring the build as
        // though it were empty.
        const itemIds = Array.isArray(build?.itemIds)
            ? build.itemIds.map((id) => (id != null ? Number(id) : 0))
            : (Array.isArray(build?.items)
                ? build.items.map((it) => (it && it.id != null ? Number(it.id) : 0))
                : []);
        const dItem = balancedItemDefenseRawTotal(itemIds);
        const dStat = defense.score;
        defenseRawScore = dStat + dItem;
        criteriaContribution = criteriaShare * criteriaScore;
        const defenseContribution = defenseShare * defenseRawScore;

        defenseTermDetails = defense.details.map((d) => ({ ...d, term: d.term * defenseShare }));
        if (dItem > 0) {
            defenseTermDetails.push({
                key: 'balanced_item_defense',
                label: { fr: 'Bonus objet defensif', en: 'Defensive item bonus', de: 'Defensiv-Gegenstandsbonus', es: 'Bonus de objeto defensivo', pt: 'Bônus de item defensivo' },
                value: dItem,
                term: dItem * defenseShare,
            });
        }
        defenseScore = defenseContribution;
        score = criteriaContribution + defenseContribution;
    }
    const criteriaWeight = defenseScore != null ? criteriaShare : 1;
    return {
        score,
        baseScore: criteriaContribution,
        criteriaScore,
        criteriaWeight,
        rawSoft,
        maxSoftScore,
        hardCount,
        statMean,
        spellMean,
        offensiveMean,
        nonOffensiveMean,
        offensiveMultFactor,
        normalizedStatsScore,
        statTerms,
        statTermDetails,
        offensiveTermDetails,
        nonOffensiveTermDetails,
        floorPenalty,
        floorTermDetails,
        spellTerms,
        spellTermDetails,
        defenseScore,
        defenseRawScore,
        defenseShare: defenseScore != null ? defenseShare : null,
        defenseTermDetails,
        varianceFactor: varianceMeta.applies ? varianceMeta.factor : 1,
        variancePenaltyPct: varianceMeta.applies ? varianceMeta.penaltyPct : 0,
        varianceApplies: varianceMeta.applies,
        gameCritPct: varianceMeta.gameCritPct,
    };
}

/**
 * Build targets map for local score display (same shape as solver /gen/ `stats_criteria`).
 * @param {import('./Build').default} build
 * @returns {Record<number, object>}
 */
export function targetsFromBuild(build) {
    if (!build || typeof build.getSolverTargets !== 'function') return {};
    return build.getSolverTargets();
}

